import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, ArrowRight, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SANS = { fontFamily: "'Nacelle', -apple-system, sans-serif", fontWeight: 400 };
const SERIF = { fontFamily: "'Nacelle', -apple-system, sans-serif", fontWeight: 600 };

const AdminLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!username || !password) {
      setError('Username and password are required');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Invalid credentials');
      }
      const data = await res.json();
      localStorage.setItem('mbe_admin_token', data.token);
      localStorage.setItem('mbe_admin_exp', String(Date.now() + data.expires_in * 1000));
      navigate('/admin');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-5" style={SANS}>
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#FF6E00]/15 border border-[#FF8533]/30 mb-4">
            <Lock className="w-6 h-6 text-[#FF8533]" />
          </div>
          <h1 className="text-white text-3xl tracking-[-1px]" style={SERIF}>
            Admin Access
          </h1>
          <p className="mt-2 text-white/50 text-sm">
            MBE Alert Analytics Dashboard
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 space-y-4"
        >
          <div>
            <label className="flex items-center gap-1.5 text-white/70 text-[12px] mb-2 tracking-[0.15em] uppercase">
              <User className="w-3 h-3" /> Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              autoComplete="username"
              spellCheck={false}
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF8533]/50 transition-colors"
              placeholder="Enter admin username"
            />
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-white/70 text-[12px] mb-2 tracking-[0.15em] uppercase">
              <Lock className="w-3 h-3" /> Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#FF8533]/50 transition-colors"
              placeholder="Enter admin password"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-rose-500/10 border border-rose-500/25 text-rose-300 text-[13px]">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#FF6E00] hover:bg-[#FF8533] disabled:opacity-60 disabled:cursor-not-allowed text-white px-5 py-3 rounded-full transition-colors"
          >
            {loading ? 'Signing in\u2026' : (
              <>
                Sign In <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-white/30 text-[11px]">
          Authorized access only.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
